import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GripVertical, Link as LinkIcon, Share2, Check, Eye } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { SHARE_PLATFORM_META, invalidateShareSettingsCache } from "@/components/ShareBar";

function LivePreview({ state }) {
  const enabled = (state.platforms || []).filter(p => p.enabled && SHARE_PLATFORM_META[p.key]);
  const showCopy = state.copy_link_enabled !== false;
  const empty = enabled.length === 0 && !showCopy;
  return (
    <div data-testid="share-live-preview"
         className="border border-dashed border-slate-300 bg-slate-50/60 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <Eye className="w-3.5 h-3.5"/>Live preview — what readers see
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">
          {enabled.length + (showCopy ? 1 : 0)} visible
        </span>
      </div>
      {/* Mimics ShareBar.jsx render */}
      <div className="bg-white border border-slate-200 rounded p-3">
        {empty ? (
          <div className="text-xs text-slate-400 italic text-center py-2">
            Nothing enabled — the share bar will be hidden entirely on blog pages.
          </div>
        ) : (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-2">Share</span>
            {enabled.map(({ key }) => {
              const { label, Icon } = SHARE_PLATFORM_META[key];
              return (
                <span key={key} aria-label={label} title={label}
                      className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                  <Icon className="w-4 h-4"/>
                </span>
              );
            })}
            {showCopy && (
              <span aria-label="Copy link" title="Copy link"
                    className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                <LinkIcon className="w-4 h-4"/>
              </span>
            )}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1">
        <Check className="w-3 h-3 text-emerald-600"/>Preview updates as you toggle and drag above.
      </p>
    </div>
  );
}

export default function ShareSettingsPanel() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/site-settings/share").then(r => setState(r.data));
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setState(next);
    setSaving(true);
    try {
      await api.put("/site-settings/share", next);
      invalidateShareSettingsCache();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      load();
    } finally { setSaving(false); }
  };

  const togglePlatform = (key) => {
    const platforms = state.platforms.map(p => p.key === key ? { ...p, enabled: !p.enabled } : p);
    persist({ ...state, platforms });
  };

  const toggleCopyLink = () => {
    persist({ ...state, copy_link_enabled: !state.copy_link_enabled });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const platforms = Array.from(state.platforms);
    const [moved] = platforms.splice(result.source.index, 1);
    platforms.splice(result.destination.index, 0, moved);
    persist({ ...state, platforms });
  };

  if (!state) return <div className="mt-6 text-slate-500">Loading…</div>;

  const enabledCount = state.platforms.filter(p => p.enabled).length + (state.copy_link_enabled ? 1 : 0);

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center justify-between gap-2 flex-wrap">
          <span className="inline-flex items-center gap-2">
            <Share2 className="w-4 h-4 text-sky-700"/>Blog share options
          </span>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-normal">
            {saving ? "Saving…" : "Auto-saves"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Choose which social platforms appear in the <strong>Share</strong> row at the top of every blog page.
          Drag to reorder — the order here is the order readers see. Currently showing <strong>{enabledCount}</strong> option{enabledCount === 1 ? "" : "s"}.
        </p>

        <LivePreview state={state}/>

        <div className="mt-6">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="share-platforms">
            {(dropProvided) => (
              <ul ref={dropProvided.innerRef} {...dropProvided.droppableProps}
                  data-testid="share-platforms-list"
                  className="space-y-2">
                {state.platforms.map((p, index) => {
                  const meta = SHARE_PLATFORM_META[p.key];
                  if (!meta) return null;
                  const Icon = meta.Icon;
                  return (
                    <Draggable key={p.key} draggableId={p.key} index={index}>
                      {(prov, snapshot) => (
                        <li ref={prov.innerRef} {...prov.draggableProps}
                            data-testid={`share-platform-row-${p.key}`}
                            className={`flex items-center gap-3 border rounded-lg p-3 bg-white transition-colors ${
                              snapshot.isDragging ? "border-sky-400 shadow-lg" : "border-slate-200"
                            }`}>
                          <span {...prov.dragHandleProps}
                                data-testid={`share-platform-drag-${p.key}`}
                                className="text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing">
                            <GripVertical className="w-4 h-4"/>
                          </span>
                          <div className="w-8 h-8 grid place-items-center text-slate-500 shrink-0">
                            <Icon className="w-4 h-4"/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading font-semibold text-slate-900 text-sm capitalize">{p.key}</div>
                            <div className="text-xs text-slate-500 truncate">{meta.label}</div>
                          </div>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <span className="text-xs uppercase tracking-widest text-slate-500">
                              {p.enabled ? "On" : "Off"}
                            </span>
                            <input type="checkbox"
                                   data-testid={`share-platform-toggle-${p.key}`}
                                   checked={p.enabled}
                                   onChange={() => togglePlatform(p.key)}
                                   className="w-4 h-4 accent-sky-700"/>
                          </label>
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {dropProvided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
        </div>

        <div className="mt-4 border-t border-slate-200 pt-4">
          <label className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer bg-white">
            <div className="w-8 h-8 grid place-items-center text-slate-500 shrink-0">
              <LinkIcon className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-slate-900 text-sm">Copy link button</div>
              <div className="text-xs text-slate-500">One-click copy the blog URL to the reader&apos;s clipboard.</div>
            </div>
            <span className="text-xs uppercase tracking-widest text-slate-500">
              {state.copy_link_enabled ? "On" : "Off"}
            </span>
            <input type="checkbox"
                   data-testid="share-copy-link-toggle"
                   checked={state.copy_link_enabled}
                   onChange={toggleCopyLink}
                   className="w-4 h-4 accent-sky-700"/>
          </label>
        </div>

        <p className="text-xs text-slate-400 mt-4">
          Changes take effect on the next blog page load. Existing tabs may need to refresh.
        </p>
      </CardContent>
    </Card>
  );
}
